import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { firestore } from "../../config/ConfigFirebase";
import { dependencies, NATURES_DEFAULT, NATURES_DEX, DEX_PREFIX } from "../../constants";
// El catálogo documental de la solicitud de pago se define junto a sus reglas
// puras (importables sin Firebase) y se reexporta aquí para que los
// consumidores sigan encontrando todos los catálogos en un solo servicio.
import { DEFAULT_PAYMENT_REQUEST_DOCS } from "../../components/contract/legal/payments/paymentRequestLogic";
// Fecha de corte mensual de las actas (payment-monthly-cutoff). Las reglas y la
// forma de la clave viven en src/paymentCutoff.js, copiado en handleContract y
// handlePaymentRequestLegal.
import { DEFAULT_PAYMENT_CUTOFF, resolvePaymentCutoff } from "../../paymentCutoff";

export { DEFAULT_PAYMENT_REQUEST_DOCS, DEFAULT_PAYMENT_CUTOFF };

// Catálogos administrables del módulo de Contratos — dependencias, naturalezas
// (por grupo, resueltas según el prefijo del código) y modalidades de
// contratación con sus tipos dependientes. Almacenados en config/contracts y
// consumidos por el frontend (este servicio) y por el backend
// (gfunctions/handleContract — validateNature lee su propia copia del doc).
// Edición solo ADMIN; lectura para usuarios autenticados.
//
// El valor persistido en el contrato sigue siendo el TEXTO (label), no la key:
// los contratos y perfiles existentes guardan textos y el filtro Firestore
// `nature in allowedNatures` compara strings. Por eso los valores se
// DESACTIVAN (no se borran) y renombrar solo afecta contratos nuevos.

const seedItem = (key, label, order) => ({ key, label, enabled: true, order });

const slug = (label) =>
  String(label)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const seedList = (labels) => labels.map((label, i) => seedItem(slug(label), label, (i + 1) * 10));

// Semillas — espejo de las listas hardcodeadas (constants.js) para que el
// comportamiento sea idéntico hasta que un admin edite la configuración.
export const DEFAULT_DEPENDENCIES = seedList(dependencies);

export const DEFAULT_NATURE_GROUPS = {
  default: seedList(NATURES_DEFAULT),
  dex: seedList(NATURES_DEX),
};

// Modalidades de contratación y tipos dependientes — levantamiento de
// requisitos de contratos de personas jurídicas (docs/contratos/requisitos.txt, R2).
export const DEFAULT_MODALITIES = [
  {
    ...seedItem("licitacion-publica", "Licitación Pública", 10),
    types: seedList([
      "Contratos de obra pública",
      "Contratos de concesión",
      "Contratos de suministro",
      "Contratos de compraventa",
      "Contratos de mantenimiento",
      "Contratos de operación o prestación de servicios no profesionales",
      "Contratos de seguros",
      "Contratos para la adquisición de bienes o servicios que no sean de características técnicas uniformes",
      "Contratos atípicos o innominados",
    ]),
  },
  {
    ...seedItem("concurso-de-meritos", "Concurso de Méritos", 20),
    types: seedList([
      "Contratos de consultoría",
      "Contratos de interventoría",
      "Contratos para la elaboración de estudios y diagnósticos",
      "Contratos de prefactibilidad o factibilidad",
      "Contratos para la elaboración de diseños, planos, anteproyectos y proyectos",
      "Contratos de gerencia de obra o de proyectos",
      "Asesorías técnicas de coordinación, control y supervisión",
      "Proyectos arquitectónicos seleccionados mediante concurso",
    ]),
  },
  {
    ...seedItem("seleccion-abreviada", "Selección Abreviada", 30),
    types: seedList([
      "Contratos de suministro",
      "Contratos de compraventa",
      "Contratos de adquisición de equipos, materiales, insumos o elementos estandarizados",
      "Contratos de prestación de servicios uniformes y de común utilización",
    ]),
  },
  {
    ...seedItem("minima-cuantia", "Mínima Cuantía", 40),
    types: seedList([
      "Contratos de obra",
      "Contratos de suministro",
      "Contratos de compraventa",
      "Contratos de mantenimiento",
      "Contratos de consultoría",
      "Contratos de interventoría",
      "Contratos de prestación de servicios",
      "Contratos de transporte",
      "Contratos de arrendamiento de bienes muebles",
      "Otros contratos de baja cuantía",
    ]),
  },
  {
    ...seedItem("contratacion-directa", "Contratación Directa", 50),
    types: seedList([
      "Servicios jurídicos",
      "Servicios contables",
      "Servicios administrativos",
      "Servicios técnicos",
      "Servicios profesionales especializados",
      "Apoyo a la gestión institucional",
      "Servicios artísticos",
    ]),
  },
];

export const DEFAULT_CONTRACT_CONFIG = {
  dependencies: DEFAULT_DEPENDENCIES,
  natureGroups: DEFAULT_NATURE_GROUPS,
  modalities: DEFAULT_MODALITIES,
  paymentRequestDocs: DEFAULT_PAYMENT_REQUEST_DOCS,
  // { enabled, noticeDays, calendar: { "YYYY-MM": { day, natures: { label: day } } } }
  paymentCutoff: DEFAULT_PAYMENT_CUTOFF,
};

// Claves que posee la pantalla de configuración de contratos. Explícitas para
// que un guardado nunca pise campos ajenos (patrón APPLICANT_CONFIG_KEYS).
export const CONTRACT_CONFIG_KEYS = Object.keys(DEFAULT_CONTRACT_CONFIG);

const pick = (obj, keys) => Object.fromEntries(keys.filter((k) => k in obj).map((k) => [k, obj[k]]));

// Lee config/contracts fusionado sobre las semillas: los consumidores siempre
// reciben un objeto completo. Devuelve las semillas ante error o ausencia.
export const getContractConfig = async () => {
  try {
    const snap = await getDoc(doc(firestore, "config", "contracts"));
    const data = snap.exists() ? snap.data() : {};
    const merged = { ...DEFAULT_CONTRACT_CONFIG, ...pick(data, CONTRACT_CONFIG_KEYS) };
    // La clave puede venir incompleta (p. ej. sin noticeDays): se normaliza
    // para que los consumidores nunca vean un objeto parcial.
    return { ...merged, paymentCutoff: resolvePaymentCutoff(merged.paymentCutoff) };
  } catch (e) {
    console.error("getContractConfig error:", e);
    return { ...DEFAULT_CONTRACT_CONFIG };
  }
};

// Escritura acotada a las claves propias. Cada clave enviada se REEMPLAZA
// completa (mergeFields), sin tocar las demás del documento. Con
// `{ merge: true }` Firestore fusiona en profundidad los mapas anidados, así que
// quitar una entrada de un mapa (una excepción de paymentCutoff.calendar, el día
// de un mes) se veía en pantalla pero nunca se borraba del documento. Todos los
// llamadores envían el valor completo de cada clave.
export const saveContractConfig = async (patch) => {
  try {
    const data = { ...pick(patch, CONTRACT_CONFIG_KEYS), updatedAt: serverTimestamp() };
    await setDoc(
      doc(firestore, "config", "contracts"),
      data,
      { mergeFields: Object.keys(data) }
    );
    return true;
  } catch (e) {
    console.error("saveContractConfig error:", e);
    return false;
  }
};

// --- Selectores (puros) ------------------------------------------------------

const sorted = (list) => (list || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

const enabledSorted = (list) => sorted(list).filter((it) => it && it.enabled !== false);

const labels = (list) => list.map((it) => it.label);

// Opciones habilitadas (labels) y variante completa para resolver valores ya
// guardados en contratos/perfiles aunque estén desactivados.
export const dependencyOptions = (cfg) => labels(enabledSorted(cfg?.dependencies));
export const allDependencyEntries = (cfg) => sorted(cfg?.dependencies);

// Naturalezas del grupo que aplica según el prefijo del código del contrato
// (misma regla que getNaturesForCode en constants.js).
const natureGroupForCode = (cfg, contractCode) =>
  contractCode?.startsWith(DEX_PREFIX) ? cfg?.natureGroups?.dex : cfg?.natureGroups?.default;

export const natureOptionsForCode = (cfg, contractCode) =>
  labels(enabledSorted(natureGroupForCode(cfg, contractCode)));

export const allNatureEntriesForCode = (cfg, contractCode) => sorted(natureGroupForCode(cfg, contractCode));

// Universo de naturalezas habilitadas (ambos grupos) — reemplaza ALL_NATURES en
// el selector de allowedNatures (UserAdmin) y el filtro visual de financiación.
export const allNatureOptions = (cfg) =>
  labels(enabledSorted([...(cfg?.natureGroups?.default || []), ...(cfg?.natureGroups?.dex || [])]));

export const allNatureEntries = (cfg) =>
  sorted([...(cfg?.natureGroups?.default || []), ...(cfg?.natureGroups?.dex || [])]);

// Modalidades y tipos dependientes. La búsqueda de la modalidad acepta key o
// label porque el contrato persiste el label.
export const modalityOptions = (cfg) => labels(enabledSorted(cfg?.modalities));
export const allModalityEntries = (cfg) => sorted(cfg?.modalities);

export const modalityByValue = (cfg, value) =>
  (cfg?.modalities || []).find((m) => m && (m.key === value || m.label === value)) || null;

export const typesForModality = (cfg, modalityValue) =>
  labels(enabledSorted(modalityByValue(cfg, modalityValue)?.types));

export const allTypeEntriesForModality = (cfg, modalityValue) =>
  sorted(modalityByValue(cfg, modalityValue)?.types);

// ¿El valor guardado está desactivado en el catálogo? (para el distintivo
// "(inactivo)" en selectores que resuelven datos existentes).
export const isInactiveValue = (entries, value) => {
  const it = (entries || []).find((e) => e && e.label === value);
  return !!it && it.enabled === false;
};

// --- Catálogo documental de la solicitud de pago -----------------------------
// Entradas habilitadas y ordenadas; allPaymentRequestDocEntries incluye las
// desactivadas para poder resolver documentos ya cargados en solicitudes
// existentes (mismo criterio que dependencias/naturalezas).
export const paymentRequestDocs = (cfg) => enabledSorted(cfg?.paymentRequestDocs);

export const allPaymentRequestDocEntries = (cfg) => sorted(cfg?.paymentRequestDocs);

export const paymentRequestDocByKey = (cfg, key) =>
  (cfg?.paymentRequestDocs || []).find((d) => d && d.key === key) || null;

// Obligatorios habilitados: los que bloquean el envío al supervisor.
export const requiredPaymentRequestDocs = (cfg) =>
  paymentRequestDocs(cfg).filter((d) => d.required !== false);

// Entradas cuyo archivo vive en la entidad y no se carga en cada solicitud.
export const entitySourcedPaymentRequestDocs = (cfg) =>
  paymentRequestDocs(cfg).filter((d) => d.source === "entity");
